import Vue from "vue";
import Data from "@/store/api/data/Data";
import Pharmacy from "@/store/api/data/Pharmacy";
import PharmacyGroup from "@/store/api/data/PharmacyGroup";

import Profit from "@/store/processors/Profit";
import Pricing from "@/store/processors/Pricing";

import { EventBus } from "@/utils/EventBus";

import mixin from "@/components/VueGlobalMixin";

export default {
    state: {
        allArticles: [], // rename to articles or products
        currentTabData: [],
        pharmacies: [],
        pharmacyGroup: [],
        manufacturer: [],
        pricingData: {}
    },
    getters: {
        knownManufacturer: state => {
            const knownManufacturerIds = state.allArticles.map(item => item.manufacturerId);
            return state.manufacturer.filter(manufacturer =>
                knownManufacturerIds.includes(manufacturer.id)
            );
        },
        totalForecast: state => {
            return state.allArticles
                .map(article => Profit.calculateArticleForecast(article))
                .reduce((a, b) => a + b, 0);
        },
        forecast: state => {
            return state.currentTabData
                .map(article => Profit.calculateArticleForecast(article))
                .reduce((a, b) => a + b, 0);
        }
    },
    mutations: {
        addOrUpdatePricingDataList(state, list) {
            list.forEach(
                ({ pzn, priceNew, name }) => (state.pricingData[pzn] = { priceNew, name })
            );
        },
        removePricingDataList(state, list) {
            list.forEach(article => (state.pricingData[article.pzn] = undefined));
        },
        resetPricingData(state) {
            state.pricingData = {};
        },
        setAllArticles(state, allArticles) {
            state.allArticles = allArticles;
        },
        changeArticle(state, article) {
            const currentTabDataIndex = state.currentTabData.findIndex(
                item => item.pzn === article.pzn
            );
            const allArticlesIndex = state.allArticles.findIndex(item => item.pzn === article.pzn);
            // Change article in both tables as currentTabData only gets modified on tab change
            Vue.set(state.allArticles, allArticlesIndex, article);
            if (currentTabDataIndex >= 0) {
                Vue.set(state.currentTabData, currentTabDataIndex, article);
            }
        },
        setCurrentTabData(state, currentTabData) {
            state.currentTabData = currentTabData;
        },
        setPharmacies(state, pharmacies) {
            state.pharmacies = pharmacies;
        },
        setPharmacyGroup(state, pharmacyGroup) {
            state.pharmacyGroup = pharmacyGroup;
        },
        setManufacturer(state, manufacturer) {
            state.manufacturer = manufacturer;
        },
        calculateArticleProfitMutation(state, article) {
            Profit.calculateArticleProfit(article);
        }
    },
    actions: {
        loadPharmacies({ commit }) {
            return Pharmacy.get().then(response => {
                commit("setPharmacies", response.data);
                commit("setSelectedPharmacy", response.data[0], { root: true });

                return response;
            });
        },
        loadPharmacyGroups({ commit, rootState }) {
            return PharmacyGroup.get(rootState.user).then(response => {
                commit("setPharmacyGroup", response.data);
                commit("setSelectedPharmacyGroup", response.data[0]);

                return response;
            });
        },

        loadArticles({ commit, dispatch, state, rootState }) {
            const promise = rootState.navSelection.isConsolidateModus
                ? Data.getMarketDataConsolidate({ ...rootState.navSelection })
                : Data.getMarketData({ ...rootState.navSelection });

            return new Promise((resolve, reject) => {
                promise.then(response => {
                    if (response.data.length === 0) {
                        reject("no article data");
                    } else {
                        const modifiedArticleList = addLocalPricingDataAndMissingAttributes(
                            response.data,
                            state
                        );
                        commit("setAllArticles", modifiedArticleList);
                        dispatch("calculateTabs");
                        resolve(modifiedArticleList);
                    }
                });
            });
        },
        calculateTabs({ commit, state, rootState }) {
            console.log("Calculating all Tabs... this may take a while");
            const tabs = rootState.tabs.tabs;
            var articles = state.allArticles;

            /* Iterate through the tabs in reverse order and set the tab index if the test function evaluates to true.
             * The idea is that earlier tabs (later tabs in reverse order) will overwrite the tab index of later tabs.
             */
            for (let index = tabs.length - 1; index >= 0; index--) {
                const testFunction = Function(
                    "article",
                    "return " + tabs[index].testFunction + ";"
                );
                try {
                    for (const article in articles) {
                        if (testFunction(articles[article]))
                            articles[article].tabId = tabs[index].id;
                    }
                } catch {
                    EventBus.$emit("send-client-error-info-to-server", {
                        title: "Testfunktion für Kartenreiter #" + tabs[index].id + " fehlerhaft",
                        msg:
                            "Kartenreiter '" + tabs[index].name + "' konnte nicht berechnet werden",
                        code: tabs[index].testFunction
                    });
                }
            }

            tabs.forEach(tab => {
                tab.articleCount = articles.filter(
                    article => article.hasOwnProperty("tabId") && article.tabId === tab.id
                ).length;
            });

            const failedArticles = articles.filter(article => !article.hasOwnProperty("tabId"));
            if (failedArticles.length > 0) {
                EventBus.$emit("send-client-error-info-to-server", {
                    title: "Es sind Artikel bei der Bepreisung übrig geblieben!",
                    articles: failedArticles
                });
            }

            commit("setAllArticles", articles);
            console.log("Calculating all Tabs... Done!");
        },
        calculateAndFilterTabData({ dispatch }) {
            return dispatch("getTabData").then(unfilteredTabData => {
                dispatch("filterTabData", unfilteredTabData);
            });
        },
        filterTabData({ commit, dispatch, rootState }, unfilteredTabData) {
            let data = unfilteredTabData;
            try {
                const filterConfig = rootState.filter;

                if (!data || data.length === 0) {
                    commit("setCurrentTabData", []);
                    return;
                }

                console.log(filterConfig);

                // Filter ArticlesNotOnStorage
                // Filter Articles if Checkbox is not selected
                if (!filterConfig.articlesNotOnStorage) {
                    data = data.filter(item => item.onStorage);
                }

                // Filter eigene Preisänderung
                if (filterConfig.priceChangeSince) {
                    const navSelection = rootState.navSelection;
                    // 0 gets the last day of previous month
                    var actualDate = new Date(navSelection.year, navSelection.month + 1, 0);
                    const priceChangeSince = parseInt(filterConfig.priceChangeSince);

                    data = data.filter(item => {
                        if (item.lastAbdaChange !== null) {
                            const itemDate = new Date(item.lastAbdaChange);
                            const monthsDiff = mixin.calculateMonthDifference(actualDate, itemDate);
                            return !(monthsDiff > 0 && monthsDiff <= priceChangeSince);
                        }
                        return true;
                    }, mixin);
                }

                // Filter Manufacturer
                const filteredManufacturerIds = filterConfig.manufacturer;
                if (filteredManufacturerIds && filteredManufacturerIds.length > 0) {
                    console.log(filteredManufacturerIds);
                    data = data.filter(
                        item => !filteredManufacturerIds.includes(item.manufacturerId)
                    );
                }
                console.log(data);
                dispatch("calculateArticleProfitsOfList", data);
                commit("setCurrentTabData", data);
            } catch (error) {
                console.error(error);
            }
        },

        loadManufacturers({ commit }) {
            return Data.getManufacturer().then(response => {
                commit("setManufacturer", response.data);
                return response;
            });
        },

        setPricing({ dispatch, state, rootState }) {
            EventBus.$emit("pricing-started");
            Pricing.setPrice(
                state.currentTabData.filter(article => !article.selected),
                rootState.pricingOptions
            ).then(articlesWithNewPrice => {
                dispatch("calculateArticleProfitsOfList", articlesWithNewPrice);
                EventBus.$emit("pricing-success");
            });
        },

        resetPricing({ dispatch, state, commit }, deletePricesInAllTabs) {
            if (deletePricesInAllTabs) {
                for (let [i, article] of state.allArticles.entries()) {
                    article.priceNew = null;
                    Vue.set(state.allArticles, i, article);
                }
                commit("resetPricingData");
            } else {
                let list = [];
                for (let [i, article] of state.currentTabData.entries()) {
                    article.priceNew = null;
                    Vue.set(state.currentTabData, i, article);
                    list.push(article);
                }
                dispatch("calculateArticleProfitsOfList", list);
            }
        },
        loadPricesToArticle({ dispatch, state }, listOfNewPrices) {
            // remove old prices
            state.allArticles
                .filter(article => article.priceNew)
                .forEach(article => {
                    article.priceNew = null;
                    dispatch("calculateArticleProfitsOfList", [article]);
                });
            // for each price in list, find the article in allArticles and price it.
            let newlyPricedArticles = [];
            listOfNewPrices.forEach(newArticlePrice => {
                let article = state.allArticles.find(
                    article => article.pzn === newArticlePrice.pzn
                );
                if (article) {
                    article.priceNew = newArticlePrice.priceNew;
                    newlyPricedArticles.push(article);
                }
            });
            dispatch("calculateArticleProfitsOfList", newlyPricedArticles);
        },
        calculateArticleProfitsOfList({ commit }, articleList) {
            console.time("Calculate"); // TODO

            let addOrUpdateList = [];
            let removeList = [];

            console.log(articleList);
            for (let article of articleList) {
                Profit.calculateArticleProfit(article);

                if (article.priceNew) addOrUpdateList.push(article);
                else removeList.push(article);
            }

            commit("addOrUpdatePricingDataList", addOrUpdateList);
            commit("removePricingDataList", removeList);

            console.timeEnd("Calculate");
        },
        getTabData({ state, rootState, dispatch }) {
            if (rootState.tabs.isPaedActive) {
                return dispatch("filterArticlesOnPaedChanged");
            } else {
                return state.allArticles.filter(
                    article => article.tabId === rootState.tabs.selectedTab.id
                );
            }
        },
        filterArticlesOnPaedChanged({ state, rootState }) {
            const now = new Date(Date.now());
            var data = state.allArticles;
            const paedChangeSince = rootState.tabs.paedChangeSince;

            if (paedChangeSince && parseInt(paedChangeSince) !== 0) {
                data = data.filter(item => {
                    if (item.lastAbdaChange) {
                        var itemDate = new Date(item.lastAbdaChange);

                        var monthsDiff = mixin.calculateMonthDifference(now, itemDate);
                        monthsDiff = (monthsDiff - 1) * 2;
                        monthsDiff += now.getDate() < 15 ? 1 : 2;
                        monthsDiff += itemDate.getDate() === 15 ? 1 : 2;

                        return !(monthsDiff > 0 && monthsDiff > parseInt(paedChangeSince));
                    } else {
                        return false;
                    }
                }, mixin);
            } else {
                data = [];
            }
            return data;
        }
    }
};

function addLocalPricingDataAndMissingAttributes(articleList, state) {
    return articleList.map(article => {
        let tempArticle = {
            priceNew: null,
            grossProfitPercent: null,
            changePercent: null,
            ...article
        };
        if (state.pricingData[article.pzn])
            tempArticle.priceNew = state.pricingData[article.pzn].priceNew;

        return tempArticle;
    });
}
